/*
 * Androvik sticky add-to-cart bar + "choose a size first" guard for Dawn 16 main-product.
 *
 * - No size is preselected unless the URL names a variant. Size radios are unchecked on load and
 *   after every Dawn re-render until the shopper picks a size. Before Dawn reads the picker on a
 *   Color change, the held size is re-checked so Dawn always requests a full option set.
 * - A sizeless submit of the main form is blocked (capture phase) and the size message is shown.
 * - The bar clicks the real size input and the real Add to cart button; it never posts to the cart
 *   itself, so Dawn's product form and cart UI do the add.
 * - Options are found by name (/size/i, /colou?r/i), never by position: Androvik's order is Color, Size.
 */
if (!customElements.get('sticky-atc-ak')) {
  const SIZE_RE = /size/i;
  const COLOR_RE = /colou?r/i;

  customElements.define(
    'sticky-atc-ak',
    class StickyAtcAk extends HTMLElement {
      connectedCallback() {
        // The theme editor swaps the main product section's DOM on edit: rebind to the new form, button and hooks.
        this.onSectionLoad = (event) => {
          if (!event.target.querySelector?.('product-info[id^="MainProduct-"]')) return;
          this.disconnectedCallback();
          this.connectedCallback();
        };
        document.addEventListener('shopify:section:load', this.onSectionLoad);

        this.info = document.querySelector('product-info[id^="MainProduct-"]');
        if (!this.info) return;
        this.sid = this.info.dataset.section;
        this.form = document.getElementById(`product-form-${this.sid}`);
        this.realBtn = document.getElementById(`ProductSubmitButton-${this.sid}`);
        if (!this.form || !this.realBtn) return;

        this.btn = this.querySelector('.satc-ak__btn');
        this.label = this.querySelector('.satc-ak__label');
        this.msg = this.querySelector('.satc-ak__msg');
        this.sizesEl = this.querySelector('.satc-ak__sizes');
        this.priceEl = this.querySelector('.satc-ak__price');
        this.chosenEl = this.querySelector('.satc-ak__chosen');
        this.thumb = this.querySelector('.satc-ak__thumb');
        this.defaultLabel = this.label.textContent.trim();

        this.sizeChosen = this.dataset.preselected === 'true' || this.sizeInputs().length === 0;
        this.heldSize = null;
        this.pending = null;

        this.onChange = this.onChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onBarTap = this.onBarTap.bind(this);
        document.addEventListener('change', this.onChange, true);
        document.addEventListener('submit', this.onSubmit, true);
        this.btn.addEventListener('click', this.onBarTap);

        if (typeof subscribe === 'function') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId === this.sid) this.onVariant(data.variant);
          });
          // Dawn shows a failed add only inside the main form, which is off-screen while the bar is up.
          this.unsubscribeError = subscribe(PUB_SUB_EVENTS.cartError, (event) => {
            if (event.source !== 'product-form' || !this.classList.contains('is-visible')) return;
            this.sizesEl.hidden = true;
            this.btn.setAttribute('aria-expanded', 'false');
            this.msg.hidden = false;
            // errors falls back to Dawn's readable description; message is a generic title such as "Cart Error".
            this.msg.textContent = (typeof event.errors === 'string' && event.errors) || event.message || '';
          });
        }

        // Dawn skips variantChange when the response has no variant (unavailable combo). This hook runs on every
        // option re-render; the microtask lands after Dawn's whole update, so it re-decorates and releases a waiting add.
        this.afterRender = () =>
          queueMicrotask(() => {
            if (!this.isConnected) return;
            this.decorate();
            this.sync();
            if (this.pending) this.pending(false);
          });
        // product-info.js loads after this script, so its class fields exist only once it is defined.
        customElements.whenDefined('product-info').then(() => {
          if (this.isConnected) this.info.preProcessHtmlCallbacks?.push(this.afterRender);
        });

        // ak: show whenever the real button is off screen, above or still below the fold, so a phone has a buy control
        // from the first screen (the button starts ~1,100 px down; QA 2026-11-05). Same as Tombrey.
        this.observer = new IntersectionObserver(([entry]) => this.toggle(!entry.isIntersecting));
        this.observer.observe(this.realBtn);
        // The bar's height (it grows while the size row is open) feeds body padding and scroll-padding in the CSS.
        this.resizer = new ResizeObserver(() =>
          document.documentElement.style.setProperty('--satc-h', `${this.offsetHeight}px`)
        );
        this.resizer.observe(this);

        this.decorate();
        this.sync();
      }

      disconnectedCallback() {
        document.removeEventListener('shopify:section:load', this.onSectionLoad);
        document.removeEventListener('change', this.onChange, true);
        document.removeEventListener('submit', this.onSubmit, true);
        this.btn?.removeEventListener('click', this.onBarTap);
        this.unsubscribe?.();
        this.unsubscribeError?.();
        this.resizer?.disconnect();
        document.documentElement.style.removeProperty('--satc-h');
        const hooks = this.info?.preProcessHtmlCallbacks;
        if (hooks?.includes(this.afterRender)) hooks.splice(hooks.indexOf(this.afterRender), 1);
        this.observer?.disconnect();
        document.body.classList.remove('satc-ak-open');
      }

      /* ---------- picker helpers ---------- */

      picker() {
        return document.getElementById(`variant-selects-${this.sid}`);
      }

      optionInputs(re) {
        const picker = this.picker();
        if (!picker) return [];
        return Array.from(picker.querySelectorAll('input[type="radio"][data-option-name]')).filter((input) =>
          re.test(input.dataset.optionName || input.closest('fieldset')?.querySelector('legend')?.textContent || '')
        );
      }

      sizeInputs() {
        return this.optionInputs(SIZE_RE);
      }

      checkedValue(re) {
        return this.optionInputs(re).find((input) => input.checked)?.value || '';
      }

      // Re-applied after every Dawn re-render of <variant-selects>.
      decorate() {
        const colorInputs = this.optionInputs(COLOR_RE);
        const colorLegend = colorInputs[0]?.closest('fieldset')?.querySelector('legend');
        if (colorLegend && !colorLegend.querySelector('[data-selected-value]')) {
          const value = document.createElement('span');
          value.dataset.selectedValue = '';
          value.textContent = colorInputs.find((input) => input.checked)?.value || '';
          colorLegend.append(': ', value);
        }

        const sizeInputs = this.sizeInputs();
        if (!sizeInputs.length) return;
        if (!this.sizeChosen) {
          const checked = sizeInputs.find((input) => input.checked);
          if (checked) this.heldSize = checked.value;
          sizeInputs.forEach((input) => (input.checked = false));
        }
      }

      motion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
      }

      /* ---------- events ---------- */

      onChange(event) {
        const input = event.target;
        if (!input.matches?.('input[type="radio"][data-option-name]')) return;
        if (!this.picker()?.contains(input)) return;

        if (SIZE_RE.test(input.dataset.optionName)) {
          this.sizeChosen = true;
          this.hideMessages();
          return;
        }
        // Color changed while no size is picked: give Dawn the held size so it requests a full option set.
        // Uncheck it as soon as Dawn has read the options (its <variant-selects> listener was added before ours),
        // so tapping that size during the fetch is a real change, not a click on an already-checked radio.
        if (!this.sizeChosen && this.heldSize) {
          const held = this.sizeInputs().find((size) => size.value === this.heldSize);
          if (!held) return;
          held.checked = true;
          this.picker().addEventListener('change', () => (held.checked = false), { once: true });
        }
      }

      onSubmit(event) {
        if (event.target !== this.form || this.sizeChosen) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.showMainMessage();
      }

      onVariant(variant) {
        // Dawn's updateURL (run just before this event) writes ?variant=<held size>; drop it until a size is
        // really chosen, so a reload or shared link doesn't silently preselect that size.
        if (!this.sizeChosen && location.search.includes('variant=')) {
          const url = new URL(location.href);
          url.searchParams.delete('variant');
          history.replaceState(history.state, '', url);
        }
        this.decorate();
        this.sync(variant);
        if (this.pending) {
          const done = this.pending;
          this.pending = null;
          done(Boolean(variant && variant.available));
        }
      }

      onBarTap() {
        if (this.btn.getAttribute('aria-busy') === 'true') return;
        if (!this.sizeChosen) {
          this.showBarSizes();
          return;
        }
        // A Dawn option fetch is still in flight (it disables the real button meanwhile): wait for its variant.
        if (this.info.pendingRequestUrl) {
          this.waitThenAdd();
          return;
        }
        if (!this.realBtn.disabled) this.realBtn.click();
      }

      /* ---------- adding ---------- */

      waitThenAdd() {
        this.btn.setAttribute('aria-busy', 'true');
        const finish = (ok) => {
          clearTimeout(timer);
          if (this.pending === finish) this.pending = null;
          this.btn.removeAttribute('aria-busy');
          if (ok) {
            // Right after this, Dawn focuses the clicked main-picker option, scrolling the page to it and hiding the
            // bar. Undo both before the next paint so the cart drawer returns focus to the bar's (visible) button.
            const y = window.scrollY;
            this.realBtn.click();
            const restore = () => {
              window.scrollTo(window.scrollX, y);
              this.btn.focus({ preventScroll: true });
            };
            queueMicrotask(restore);
            // media-gallery.setActiveMedia queued a setTimeout(0) scroll-to-image before variantChange published;
            // a timer queued now runs after it and cancels its smooth scroll.
            setTimeout(restore);
          } else {
            this.realBtn.scrollIntoView({ block: 'center', behavior: this.motion() });
            this.realBtn.focus({ preventScroll: true }); // the bar's size row is gone; don't drop focus to <body>
          }
        };
        // Never add a stale variant: on timeout, send the shopper to the real button instead.
        const timer = setTimeout(() => finish(false), 8000);
        this.pending = finish;
      }

      showBarSizes() {
        const sizes = this.sizeInputs();
        if (!sizes.length) {
          this.realBtn.click();
          return;
        }
        this.sizesEl.replaceChildren(
          ...sizes.map((input) => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'satc-ak__size';
            button.textContent = input.value;
            if (input.classList.contains('disabled')) button.disabled = true;
            button.addEventListener('click', () => this.pickSize(input.value));
            return button;
          })
        );
        this.sizesEl.style.setProperty('--satc-cols', sizes.length <= 5 ? sizes.length : Math.ceil(sizes.length / 2));
        this.msg.hidden = false;
        this.msg.textContent = this.dataset.sizeMessage; // re-set so the live region announces it
        this.sizesEl.hidden = false;
        this.btn.setAttribute('aria-expanded', 'true');
        this.sizesEl.querySelector('button:not([disabled])')?.focus();
      }

      pickSize(value) {
        const input = this.sizeInputs().find((size) => size.value === value);
        if (!input) return;
        this.btn.focus(); // keep focus in the bar: the size row holding the focused button is about to hide
        this.waitThenAdd();
        input.click(); // fires change: Dawn fetches the variant, onVariant() then completes the add
      }

      hideMessages() {
        this.btn.setAttribute('aria-expanded', 'false');
        this.msg.hidden = true;
        this.sizesEl.hidden = true;
        this.info.querySelectorAll('.ak-size-msg').forEach((node) => node.remove());
      }

      showMainMessage() {
        const fieldset = this.sizeInputs()[0]?.closest('fieldset');
        if (!fieldset) return;
        let note = fieldset.querySelector('.ak-size-msg');
        if (!note) {
          note = document.createElement('p');
          note.className = 'ak-size-msg';
          note.setAttribute('role', 'alert');
          fieldset.append(note);
        }
        note.textContent = this.dataset.sizeMessage;
        fieldset.classList.remove('ak-flash');
        void fieldset.offsetWidth; // restart the flash animation
        fieldset.classList.add('ak-flash');
        fieldset.scrollIntoView({ block: 'center', behavior: this.motion() });
      }

      /* ---------- view ---------- */

      toggle(show) {
        this.classList.toggle('is-visible', show);
        document.body.classList.toggle('satc-ak-open', show);
      }

      sync(variant) {
        // Before a size is chosen the real button reflects a held, unseen size: keep the bar tappable and generic.
        const realLabel = this.sizeChosen && this.realBtn.querySelector('span')?.textContent.trim();
        this.label.textContent = realLabel || this.defaultLabel;
        this.btn.disabled = this.sizeChosen && this.realBtn.disabled;

        const price = document.getElementById(`price-${this.sid}`);
        const priceItem =
          price?.querySelector('.price--on-sale .price__sale .price-item--last') ||
          price?.querySelector('.price__regular .price-item--regular');
        if (priceItem) this.priceEl.textContent = priceItem.textContent.trim();

        const src = variant?.featured_image?.src;
        if (src && this.thumb) this.thumb.src = `${src}${src.includes('?') ? '&' : '?'}width=120`;

        const size = this.sizeChosen ? this.checkedValue(SIZE_RE) : '';
        const sizeName = this.sizeInputs()[0]?.dataset.optionName || '';
        const color = this.checkedValue(COLOR_RE);
        this.chosenEl.textContent = [color, size && `${sizeName} ${size}`].filter(Boolean).join(' · ');
        this.chosenEl.hidden = !size;
      }
    }
  );
}
